import { useMemo } from 'react';
import { useEntities } from './useSWR';

/**
 * Custom hook to dynamically fetch lookup data based on configuration
 * Scalable solution that supports up to 25 lookup endpoints
 * 
 * To increase the limit beyond 25:
 * 1. Add more lookup variables (lookup26, lookup27, etc.)
 * 2. Add them to the lookupResults array
 * 3. Add them to the dependency array
 * 4. Update the warning threshold
 * 
 * @param {Object} lookupEndpoints - Object with lookup endpoint keys and values
 * @returns {Object} Lookup data keyed by endpoint names
 */
export const useDynamicLookups = (lookupEndpoints = {}) => {
  // Create a stable, sorted array of lookup entries to ensure consistent hook order
  const lookupEntries = useMemo(() => {
    return Object.entries(lookupEndpoints).sort(([a], [b]) => a.localeCompare(b));
  }, [lookupEndpoints]);

  // Call exactly 25 hooks unconditionally to satisfy React's rules
  // This provides 25 lookup slots - can be increased if needed
  const lookup1 = useEntities(lookupEntries[0]?.[1] || null);
  const lookup2 = useEntities(lookupEntries[1]?.[1] || null);
  const lookup3 = useEntities(lookupEntries[2]?.[1] || null);
  const lookup4 = useEntities(lookupEntries[3]?.[1] || null);
  const lookup5 = useEntities(lookupEntries[4]?.[1] || null);
  const lookup6 = useEntities(lookupEntries[5]?.[1] || null);
  const lookup7 = useEntities(lookupEntries[6]?.[1] || null);
  const lookup8 = useEntities(lookupEntries[7]?.[1] || null);
  const lookup9 = useEntities(lookupEntries[8]?.[1] || null);
  const lookup10 = useEntities(lookupEntries[9]?.[1] || null);
  const lookup11 = useEntities(lookupEntries[10]?.[1] || null);
  const lookup12 = useEntities(lookupEntries[11]?.[1] || null);
  const lookup13 = useEntities(lookupEntries[12]?.[1] || null);
  const lookup14 = useEntities(lookupEntries[13]?.[1] || null);
  const lookup15 = useEntities(lookupEntries[14]?.[1] || null);
  const lookup16 = useEntities(lookupEntries[15]?.[1] || null);
  const lookup17 = useEntities(lookupEntries[16]?.[1] || null);
  const lookup18 = useEntities(lookupEntries[17]?.[1] || null);
  const lookup19 = useEntities(lookupEntries[18]?.[1] || null);
  const lookup20 = useEntities(lookupEntries[19]?.[1] || null);
  const lookup21 = useEntities(lookupEntries[20]?.[1] || null);
  const lookup22 = useEntities(lookupEntries[21]?.[1] || null);
  const lookup23 = useEntities(lookupEntries[22]?.[1] || null);
  const lookup24 = useEntities(lookupEntries[23]?.[1] || null);
  const lookup25 = useEntities(lookupEntries[24]?.[1] || null);
  
  // Combine results into a memoized object
  const lookupData = useMemo(() => {
    const result = {};
    const lookupResults = [
      lookup1, lookup2, lookup3, lookup4, lookup5, lookup6, lookup7, lookup8, lookup9, lookup10,
      lookup11, lookup12, lookup13, lookup14, lookup15, lookup16, lookup17, lookup18, lookup19, lookup20,
      lookup21, lookup22, lookup23, lookup24, lookup25
    ];
    
    lookupEntries.forEach(([key], index) => {
      if (index < lookupResults.length && lookupResults[index]?.data) {
        result[key] = lookupResults[index].data;
      }
    });
    
    
    return result;
  }, [
    lookupEntries, 
    lookup1, lookup2, lookup3, lookup4, lookup5, lookup6, lookup7, lookup8, lookup9, lookup10,
    lookup11, lookup12, lookup13, lookup14, lookup15, lookup16, lookup17, lookup18, lookup19, lookup20,
    lookup21, lookup22, lookup23, lookup24, lookup25
  ]);

  return lookupData;
};
